import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUpRight, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { LocalTime } from '@/components/generals/local-time'
import { siteConfig } from '@/constants/site'

const pillImages = [
    { src: '/images/hero-sketch.jpg', position: 'center 42%' },
    { src: '/images/nico-portrait.jpg', position: 'center 22%' },
    { src: '/images/hero-lisbon.jpg', position: 'center 62%' }
]

/** The rounded photo that sits inside the headline and quietly cycles through a few images. */
const HeroPill: FC = (): ReactNode => {
    const [active, setActive] = useState(0)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const interval = window.setInterval(() => {
            setActive(index => (index + 1) % pillImages.length)
        }, 2600)

        return () => window.clearInterval(interval)
    }, [])

    return (
        <span
            aria-hidden='true'
            className='relative mx-[0.1em] inline-block h-[0.74em] w-[1.45em] -translate-y-[0.07em] overflow-hidden rounded-full bg-muted align-middle'
        >
            {pillImages.map((image, index) => (
                <Image
                    key={image.src}
                    src={image.src}
                    alt=''
                    fill
                    sizes='(min-width: 768px) 280px, 90px'
                    preload={index === 0}
                    className={cn(
                        'object-cover transition-opacity duration-700 ease-out',
                        index === active ? 'opacity-100' : 'opacity-0'
                    )}
                    style={{ objectPosition: image.position }}
                />
            ))}
        </span>
    )
}

const AvailabilityBadge: FC = (): ReactNode => {
    return (
        <Link
            href='#contact'
            aria-label={`${siteConfig.availability}. Start a project`}
            className='group relative grid size-28 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground shadow-[0_24px_60px_-24px_rgba(43,53,255,0.8)] md:size-36'
        >
            <svg viewBox='0 0 100 100' aria-hidden='true' className='spin-slow absolute inset-0 size-full'>
                <defs>
                    <path id='availability-circle' d='M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0' />
                </defs>

                <text className='fill-current font-mono text-[8px] font-medium uppercase tracking-[0.18em]'>
                    <textPath href='#availability-circle' textLength='234' lengthAdjust='spacing'>
                        Open for projects • Jan 2027 •
                    </textPath>
                </text>
            </svg>

            <ArrowUpRight className='size-7 transition-transform duration-500 group-hover:rotate-45 md:size-9' />
        </Link>
    )
}

export const Hero: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate overflow-hidden pb-14 pt-24 md:pb-20 md:pt-32'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -right-[18vw] -top-[22vw] -z-10 size-[62vw] rounded-full bg-brand/[0.08] blur-3xl'
            />

            <div className='page-container'>
                <div className='rise-in flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-foreground/15 pb-5 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground'>
                    <span>{siteConfig.role}</span>

                    <span className='inline-flex items-center gap-1.5'>
                        <MapPin className='size-3.5' />
                        Lisbon
                        <LocalTime className='text-foreground' />
                    </span>

                    <span className='hidden sm:inline'>Portfolio 2017–2026</span>
                </div>

                <h1 className='mt-10 font-heading text-[clamp(3rem,13.2vw,13.5rem)] font-semibold leading-[0.86] tracking-[-0.045em] md:mt-14'>
                    <span className='-my-[0.14em] block overflow-hidden py-[0.14em]'>
                        <span className='line-reveal block' style={{ animationDelay: '120ms' }}>
                            Websites<HeroPill />
                        </span>
                    </span>

                    <span className='-my-[0.14em] block overflow-hidden py-[0.14em] md:pl-[14vw]'>
                        <span className='line-reveal block' style={{ animationDelay: '260ms' }}>
                            with a <em className='font-serif font-normal italic tracking-[-0.02em] text-brand'>pulse</em><span className='text-brand'>.</span>
                        </span>
                    </span>
                </h1>

                <div className='mt-12 grid items-end gap-10 md:mt-20 md:grid-cols-12'>
                    <p
                        className='rise-in max-w-xl text-lg leading-relaxed text-foreground/75 md:col-span-6 md:text-xl lg:col-span-5'
                        style={{ animationDelay: '520ms' }}
                    >
                        I'm Nico, an independent designer crafting bold, fast and memorable websites for brands with something to say. Strategy, art direction and build, all under one roof.
                    </p>

                    <div
                        className='rise-in flex flex-wrap gap-3 md:col-span-6 lg:col-span-4'
                        style={{ animationDelay: '640ms' }}
                    >
                        <ButtonLink
                            href='#work'
                            size='lg'
                            icon={<ArrowDown className='size-4 transition-transform duration-300 group-hover/button:translate-y-0.5' />}
                        >
                            See selected work
                        </ButtonLink>

                        <ButtonLink href='#contact' variant='outline' size='lg'>
                            Start a project
                        </ButtonLink>
                    </div>

                    <div
                        className='rise-in flex md:col-span-12 lg:col-span-3 lg:justify-end'
                        style={{ animationDelay: '760ms' }}
                    >
                        <AvailabilityBadge />
                    </div>
                </div>
            </div>
        </section>
    )
}
